import React, { useEffect } from "react";
import { ChakraProvider } from "@chakra-ui/react";
import type { AppProps } from "next/app";
import Script from "next/script";
import { Intercom, LiveChatLoaderProvider } from "react-live-chat-loader";
import { Provider as BalancerProvider } from "react-wrap-balancer";
import * as Sentry from "@sentry/nextjs";
import { theme } from "theme/theme";
import { GlobalStyles } from "theme/global";
import { identify } from "lib/analytics";
import { HeaderHeightProvider } from "contexts/header-height";
import { HtEvents } from "components/ht-events";
import type { HtEventsBrowser } from "@ht-sdks/events-sdk-js-browser";

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    hbspt?: {
      forms: {
        create: (options: unknown) => Record<string, unknown>;
      };
    };
    dataLayer?: any;
    intercomSettings?: {
      app_id: string;
      name: string;
      email: string;
      user_id: number;
    };
    Intercom?: Intercom;
    OneTrust?: any;
    ChiliPiper?: any;
    htevents?: HtEventsBrowser;
    lintrk?: any;
    mutiny?: any;
    ko?: any;
    fbq?: any;
    rdt?: any;
  }

  type Intercom = {
    (method: "update", user?: Record<string, unknown>): void;
    (method: "showNewMessage", message?: string): void;
  };
}

type GraphQlResponse = {
  data?: {
    getCurrentUserV2:
      | {
          id: number;
          email: string;
          name: string;
        }
      | undefined;
  };
};

export default function App({ Component, pageProps }: AppProps) {
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const load = async () => {
      const response = await fetch("https://api.hightouch.com/api/v1/graphql", {
        method: "post",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          "x-hasura-role": "user",
        },
        body: JSON.stringify({
          query: `
            query {
              getCurrentUserV2 {
                id
                email
                name
              }
            }
          `,
        }),
      });

      const body: GraphQlResponse = await response.json();
      const user = body.data?.getCurrentUserV2;

      // If there is an authenticated app user, set their information for Intercom
      // The Aloglia integration also references the user ID from these settings
      if (user) {
        window.intercomSettings = {
          app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID ?? "",
          name: user.name,
          email: user.email,
          user_id: user.id,
        };

        // eslint-disable-next-line new-cap
        window.Intercom?.("update");

        identify(String(user.id), {
          email: user.email,
          name: user.name,
        });

        Sentry.setUser({ email: user.email, id: user.id });
      }
    };

    void load();
  }, []);

  return (
    <>
      <LiveChatLoaderProvider provider="intercom" providerKey={process.env.NEXT_PUBLIC_INTERCOM_APP_ID ?? ""}>
        <HeaderHeightProvider>
          <BalancerProvider>
            <ChakraProvider theme={theme}>
              <GlobalStyles />
              <Component {...pageProps} />
              <Intercom color="#00a341" />
              <Script src="/third-party-scripts/google-tag-manager.js" strategy="lazyOnload" />
              <Script src="/third-party-scripts/mutiny.js" strategy="beforeInteractive" />
              <Script
                data-cfasync="false"
                src="https://client-registry.mutinycdn.com/personalize/client/06886c201d74e25e.js"
                strategy="beforeInteractive"
              />
            </ChakraProvider>
          </BalancerProvider>
        </HeaderHeightProvider>
      </LiveChatLoaderProvider>
      <HtEvents />
    </>
  );
}
